import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from '../components/markdownComponents';

const DOCS: Record<string, { file: string; title: string }> = {
  'executive-summary': { file: '/docs/VRPSaaS-Executive-Summary.md', title: 'VRPSaaS Executive Summary' },
  'unified-spec': { file: '/docs/VRPSaaS-Unified-Spec.md', title: 'VRPSaaS Unified Spec' },
};

export default function DocPage() {
  const { docId } = useParams<{ docId: string }>();
  const doc = docId ? DOCS[docId] : undefined;
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!doc) return;
    setContent(null);
    setError(false);
    fetch(doc.file)
      .then(r => { if (!r.ok) throw new Error(); return r.text(); })
      .then(setContent)
      .catch(() => setError(true));
  }, [doc]);

  if (!doc) {
    return <Typography color="text.secondary">Document not found.</Typography>;
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>{doc.title}</Typography>
      {error ? (
        <Typography color="error">Failed to load document.</Typography>
      ) : content === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{
          '& h1,& h2,& h3,& h4': { mt: 3, mb: 1, fontWeight: 600 },
          '& h1': { fontSize: '1.6rem' },
          '& h2': { fontSize: '1.3rem' },
          '& h3': { fontSize: '1.1rem' },
          '& p': { my: 1, lineHeight: 1.7 },
          '& ul,& ol': { pl: 3, my: 1 },
          '& li': { mb: 0.5 },
          '& code': { fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: '0.875em' },
          '& pre': { bgcolor: 'action.hover', p: 2, borderRadius: 1, overflow: 'auto', '& code': { bgcolor: 'transparent', p: 0 } },
          '& blockquote': { borderLeft: 4, borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary' },
          '& table': { borderCollapse: 'collapse', width: '100%', my: 2 },
          '& th,& td': { border: 1, borderColor: 'divider', px: 1.5, py: 0.75, textAlign: 'left' },
          '& th': { bgcolor: 'action.hover', fontWeight: 600 },
          '& hr': { my: 3, borderColor: 'divider' },
          '& a': { color: 'primary.main' },
        }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</ReactMarkdown>
        </Box>
      )}
    </Box>
  );
}
